import React, { useMemo, useState } from 'react';

import { Grid, Typography } from '@mui/material';
import Button from '@mui/material/Button';

import { MonthString, YearMonth, YearString } from 'appTypes';
import dayjs from 'dayjs';

import { monthToName } from '../datetimeUtils';
import { ALL_MONTHS, buildYearMonth, fillYears, parseYearMonth } from './utils';

import styles from './DateTimeSelector.module.css';

interface IMonthYearMenu {
  defaultValue: YearMonth;
  minDate: YearMonth;
  maxDate: YearMonth;
  onChange?: (d: YearMonth) => void;
}

export function MonthYearMenu({
  defaultValue,
  minDate,
  maxDate,
  onChange,
}: IMonthYearMenu) {
  const [currDate, setCurrDate] = useState<YearMonth>(defaultValue);

  const minYear = useMemo(() => minDate.split('-')[0] as YearString, [minDate]);

  const { year: currYear } = useMemo(
    () => parseYearMonth(currDate),
    [currDate],
  );

  const { maxYear, maxMonth } = useMemo(() => {
    const [y, m] = maxDate.split('-').map(Number);
    return { maxYear: y, maxMonth: m };
  }, [maxDate]);

  const years = useMemo(
    () => fillYears(Number(minYear), Number(maxYear)),
    [minYear, maxYear],
  );

  const update = (date: { year?: string; month?: MonthString }) => {
    setCurrDate((prev) => {
      const [y, m] = prev.split('-');
      let next = buildYearMonth(date.year ?? y, date.month ?? m);

      if (dayjs(next).isAfter(dayjs(maxDate))) next = maxDate;
      else if (dayjs(next).isBefore(dayjs(minDate))) next = minDate;

      onChange?.(next);
      return next;
    });
  };

  const handleMonthClick = (month: MonthString) => update({ month });
  const handleYearClick = (year: string) => update({ year });

  const getDisabled = (month: MonthString) => {
    const isMaxYear = currYear === maxYear;
    const monthNum = Number(month);

    const minDayjs = dayjs(minDate);
    const maxDayjs = dayjs(maxDate);
    // months below maxMonth are always enabled
    if (isMaxYear && monthNum < maxMonth) return false;
    const curr = dayjs(buildYearMonth(currYear, month));
    if (!curr.isBefore(minDayjs) && !curr.isAfter(maxDayjs)) return false;
    return true;
  };

  return (
    <Grid
      container
      spacing={1}
      className={styles.time_picker}
      columns={{ xs: 12 }}
      sx={{ width: '240px' }}
    >
      <Typography variant="caption" className={styles.time_selector_typography}>
        Month
      </Typography>

      <Grid container className={styles.gridContainer}>
        {ALL_MONTHS.map((m) => (
          <Grid item key={m} xs={3}>
            <Button
              className={styles.hour_button}
              id={
                currDate.split('-')[1] === m ? styles.button_active : undefined
              }
              disabled={getDisabled(m)}
              onClick={() => handleMonthClick(m)}
            >
              <Typography variant="caption">{monthToName(m)}</Typography>
            </Button>
          </Grid>
        ))}
      </Grid>

      <Typography variant="caption" className={styles.time_selector_typography}>
        Year
      </Typography>

      <Grid container className={styles.gridContainer}>
        {years.map((y) => (
          <Grid item key={y} xs={3}>
            <Button
              className={styles.hour_button}
              id={
                currDate.split('-')[0] === y ? styles.button_active : undefined
              }
              onClick={() => handleYearClick(y)}
            >
              <Typography variant="caption">{y}</Typography>
            </Button>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
}
